// hooks/useInfiniteScroll.ts
"use client";

import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteScrollOptions {
  /**
   * Function to call when intersection is detected to load more content
   */
  fetchNextPage?: () => void | Promise<any>;

  /**
   * Whether there is more content to load
   */
  hasNextPage?: boolean;

  /**
   * IntersectionObserver threshold (0 to 1) - percentage of element visible to trigger
   */
  threshold?: number;

  /**
   * IntersectionObserver root margin
   */
  rootMargin?: string;

  /**
   * Optional container reference to use as the viewport
   */
  rootRef?: RefObject<Element | null> | null;

  /**
   * Whether infinite scrolling is enabled
   */
  enabled?: boolean;
}

/**
 * Hook for implementing infinite scrolling with IntersectionObserver
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>({
  fetchNextPage,
  hasNextPage = false,
  threshold = 0.1,
  rootMargin = "0px",
  rootRef = null,
  enabled = true,
}: UseInfiniteScrollOptions) {
  const targetRef = useRef<T>(null);
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  const fetchNextPageRef = useRef(fetchNextPage);

  useEffect(() => {
    fetchNextPageRef.current = fetchNextPage;
  }, [fetchNextPage]);

  const resetFetchingState = useCallback(() => {
    isFetchingRef.current = false;
    setIsFetching(false);
  }, []);

  useEffect(() => {
    if (!enabled || !hasNextPage || !fetchNextPageRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isFetchingRef.current) {
            // Skip if target is in a hidden/zero-size container (e.g., inactive CSS tab)
            const rect = entry.target.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) return;

            isFetchingRef.current = true;
            setIsFetching(true);
            Promise.resolve(fetchNextPageRef.current!()).finally(() => {
              isFetchingRef.current = false;
              setIsFetching(false);
            });
          }
        });
      },
      {
        root: rootRef?.current || null,
        rootMargin,
        threshold,
      }
    );

    const target = targetRef.current;
    if (target) {
      observer.observe(target);
      return () => {
        observer.unobserve(target);
        observer.disconnect();
      };
    }
    // isFetching is a dep so the observer is rebuilt once each fetch settles: a
    // fresh observer re-fires on a still-intersecting sentinel, auto-continuing
    // until the viewport fills. Without it, a first page shorter than the
    // viewport stalls (the stable observer never sees an intersection change).
  }, [hasNextPage, threshold, rootMargin, rootRef, enabled, isFetching]);

  return {
    ref: targetRef,
    isFetching,
    resetFetchingState,
  };
}
